// The bell in the app bar: match alerts by e-mail, off / ≥ 80 % / ≥ 90 %.
// Personal (each person picks their own level), hence next to the login and
// not among the company settings. Takes effect at once. Logic in ./alerts.ts.
import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, CircularProgress, FormControlLabel, IconButton, Popover, Radio, RadioGroup, Tooltip, Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { AlertState, DEFAULT_LEVELS, describeAlertState, fetchAlertState, levelLabel, saveAlertLevel } from './alerts';

const AlertsBell: React.FC<{ auth: any }> = ({ auth }) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [state, setState] = useState<AlertState | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setBusy(true);
        setError(null);
        try {
            setState(await fetchAlertState(auth));
        } catch (e: any) {
            setError(e?.message || 'Could not read your alert setting');
        } finally {
            setBusy(false);
        }
    }, [auth]);

    // Read on first open only; the state is small and rarely changes.
    useEffect(() => {
        if (anchor && !state) void load();
    }, [anchor, state, load]);

    const choose = async (level: number) => {
        setBusy(true);
        setError(null);
        try {
            setState(await saveAlertLevel(auth, level));
        } catch (e: any) {
            setError(e?.message || 'Could not save your alert setting');
        } finally {
            setBusy(false);
        }
    };

    const levels = state?.levels?.length ? state.levels : DEFAULT_LEVELS;
    const active = state?.status === 'active';
    return (
        <>
            <Tooltip title="Match alerts by e-mail">
                <IconButton color="inherit" size="small" aria-label="match alerts" onClick={(e) => setAnchor(e.currentTarget)}>
                    {active ? <NotificationsActiveIcon fontSize="small" /> : <NotificationsNoneIcon fontSize="small" />}
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ p: 2, width: 330 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Match alerts by e-mail</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        to {state?.email || auth?.user?.profile?.email || 'your login e-mail'}
                    </Typography>
                    {busy && !state ? (
                        <CircularProgress size={18} />
                    ) : (
                        <RadioGroup value={state?.level ?? 0} onChange={(_e, value) => { void choose(Number(value)); }}>
                            {[0, ...levels].map((level) => (
                                <FormControlLabel
                                    key={level} value={level} control={<Radio size="small" />}
                                    label={levelLabel(level)} disabled={busy || state?.available === false}
                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.82rem' } }} />
                            ))}
                        </RadioGroup>
                    )}
                    {state && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{describeAlertState(state)}</Typography>
                    )}
                    {error && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{error}</Typography>}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Takes effect at once. One mail per day, only tenders you have not been told about. Every mail has an unsubscribe link.
                    </Typography>
                </Box>
            </Popover>
        </>
    );
};

export default AlertsBell;
